import { BaseEntity } from './base.entity';
import { UserRole } from '../types/user-role.enum';

export type ProjectUserRole = UserRole;

export class ProjectUser extends BaseEntity {
  id: string;
  project_id: string;
  user_id: string;
  role: ProjectUserRole;
  joined_at: Date;
  created_at: Date;
  updated_at: Date;

  constructor(data: {
    id: string;
    project_id: string;
    user_id: string;
    role: ProjectUserRole;
    joined_at: Date;
    created_at: Date;
    updated_at: Date;
  }) {
    super();
    this.id = data.id;
    this.project_id = data.project_id;
    this.user_id = data.user_id;
    this.role = data.role;
    this.joined_at = data.joined_at;
    this.created_at = data.created_at;
    this.updated_at = data.updated_at;
    this.setOriginalData(this.toData());
  }

  /**
   * Update user role
   */
  updateRole(role: ProjectUserRole): void {
    if (this.role === UserRole.OWNER && role !== UserRole.OWNER) {
      throw new Error('Cannot change owner role without transferring ownership');
    }
    this.role = role;
    this.updated_at = new Date();
    this.markAsDirty();
  }

  /**
   * Check if user has permission
   */
  hasPermission(permission: 'view' | 'edit' | 'manage'): boolean {
    switch (permission) {
      case 'view':
        return true; // All roles can view
      case 'edit':
        return this.role !== UserRole.VIEWER;
      case 'manage':
        return this.role === UserRole.OWNER || this.role === UserRole.ADMIN;
      default:
        return false;
    }
  }

  /**
   * Convert to plain data object
   */
  toData(): any {
    return {
      id: this.id,
      project_id: this.project_id,
      user_id: this.user_id,
      role: this.role,
      joined_at: this.joined_at,
      created_at: this.created_at,
      updated_at: this.updated_at
    };
  }

  /**
   * Create ProjectUser instance from database row
   */
  static fromData(data: any): ProjectUser {
    return new ProjectUser({
      id: data.id,
      project_id: data.project_id,
      user_id: data.user_id,
      role: data.role,
      joined_at: new Date(data.joined_at),
      created_at: new Date(data.created_at),
      updated_at: new Date(data.updated_at)
    });
  }
}

export interface ProjectUserInsert {
  project_id: string;
  user_id: string;
  role: ProjectUserRole;
}

export interface ProjectUserUpdate {
  role?: ProjectUserRole;
}